import { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { NumericFormatCustom } from '../../../components/NumericCustomFormatter';
import {
  BundleComponentItem,
  BundleComponentType,
  createLocalId,
} from './types';
import { AddOnProduct } from '../../../store/addonProduct/api/interface';
import { CategoryCake } from '../../../store/categoryCake/api/interface';
import { calculateCakeUnitPrice } from '../../../utils/calculateCakeUnitPrice';
import { CURRENCY } from '../../../constants';
import { fCurrencyQAR } from '../../../utils/formatNumber';

type ProductOption = AddOnProduct | CategoryCake;

const getCakeUnitPrice = (cake: CategoryCake): number =>
  calculateCakeUnitPrice(
    cake.shape,
    cake.flavor,
    cake.topping_category_item
  );

const getProductLabel = (
  product: ProductOption,
  componentType: BundleComponentType
): string => {
  if (componentType === 'addon') {
    const addon = product as AddOnProduct;
    return `${addon.labelEn} — ${fCurrencyQAR(addon.price)}`;
  }
  const cake = product as CategoryCake;
  return `${cake.labelEn} — ${fCurrencyQAR(getCakeUnitPrice(cake))}`;
};

const filterProductOptions = (
  options: ProductOption[],
  inputValue: string
): ProductOption[] => {
  const search = inputValue.toLowerCase().trim();
  if (!search) return options;

  return options.filter(
    (option) =>
      option.labelEn?.toLowerCase().includes(search) ||
      option.labelAr?.toLowerCase().includes(search)
  );
};

interface Props {
  components: BundleComponentItem[];
  onChange: (components: BundleComponentItem[]) => void;
  addonProducts: AddOnProduct[];
  occasionCakes: CategoryCake[];
  computedTotal: number;
  overridePrice: string;
  onOverridePriceChange: (value: string) => void;
  finalPrice: number;
  disabled?: boolean;
}

const BundleComponentsTab = ({
  components,
  onChange,
  addonProducts,
  occasionCakes,
  computedTotal,
  overridePrice,
  onOverridePriceChange,
  finalPrice,
  disabled = false,
}: Props) => {
  const [componentType, setComponentType] =
    useState<BundleComponentType>('addon');
  const [selectedProduct, setSelectedProduct] = useState<ProductOption | null>(
    null
  );

  const productOptions: ProductOption[] =
    componentType === 'addon'
      ? addonProducts
      : occasionCakes.filter((cake) => cake.activeState !== false);

  const handleAddComponent = () => {
    if (disabled || !selectedProduct) return;

    if (componentType === 'addon') {
      const selectedAddon = selectedProduct as AddOnProduct;

      onChange([
        ...components,
        {
          id: createLocalId(),
          componentType: 'addon',
          productId: selectedAddon.id,
          labelEn: selectedAddon.labelEn,
          labelAr: selectedAddon.labelAr,
          qty: 1,
          unitPrice: Number(selectedAddon.price || 0),
        },
      ]);
    } else {
      const selectedCake = selectedProduct as CategoryCake;

      onChange([
        ...components,
        {
          id: createLocalId(),
          componentType: 'cake',
          productId: selectedCake.id,
          labelEn: selectedCake.labelEn,
          labelAr: selectedCake.labelAr,
          qty: 1,
          unitPrice: getCakeUnitPrice(selectedCake),
        },
      ]);
    }

    setSelectedProduct(null);
  };

  const handleRemove = (id: string) => {
    onChange(components.filter((item) => item.id !== id));
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0, overflowX: 'hidden' }}>
      {disabled && (
        <Alert
          severity='info'
          sx={{ mb: 2 }}
        >
          Variants Components are in use. Remove all variant groups to add bundle
          components.
        </Alert>
      )}

      <Box
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          mb: 2,
          opacity: disabled ? 0.55 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        <Grid
          container
          spacing={2}
          alignItems='flex-end'
        >
          <Grid
            item
            xs={12}
            md={2}
          >
            <Typography
              variant='body2'
              sx={{ mb: 0.5 }}
            >
              Type
            </Typography>
            <ToggleButtonGroup
              exclusive
              size='small'
              value={componentType}
              onChange={(_, value) => {
                if (value) {
                  setComponentType(value);
                  setSelectedProduct(null);
                }
              }}
              fullWidth
              sx={{
                '& .MuiToggleButton-root.Mui-selected': {
                  bgcolor: '#FDF0F1',
                  '&:hover': {
                    bgcolor: '#FDF0F1',
                  },
                },
              }}
            >
              <ToggleButton value='addon'>Addon</ToggleButton>
              <ToggleButton value='cake'>Cake</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid
            item
            xs={12}
            md={7}
          >
            <Typography
              variant='body2'
              sx={{ mb: 0.5 }}
            >
              Select Product
            </Typography>
            <Autocomplete<ProductOption>
              fullWidth
              size='small'
              options={productOptions}
              value={selectedProduct}
              onChange={(_, newValue) => setSelectedProduct(newValue)}
              getOptionLabel={(option) => getProductLabel(option, componentType)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, { inputValue }) =>
                filterProductOptions(options, inputValue)
              }
              noOptionsText='No products found'
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder='Choose product...'
                />
              )}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={3}
          >
            <Typography
              variant='body2'
              sx={{ mb: 0.5, visibility: 'hidden' }}
            >
              Add
            </Typography>
            <Button
              fullWidth
              variant='contained'
              onClick={handleAddComponent}
              sx={{ height: 40 }}
            >
              + Add
            </Button>
          </Grid>
        </Grid>
      </Box>

      {components.length === 0 ? (
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ py: 3, textAlign: 'center' }}
        >
          No products added yet. Use the row above to add items.
        </Typography>
      ) : (
        <TableContainer
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Table
            size='small'
            sx={{ minWidth: 420 }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Type</TableCell>
                <TableCell>Product</TableCell>
                <TableCell
                  align='right'
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Unit Price
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {components.map((item) => (
                <TableRow key={item.id}>
                  <TableCell
                    sx={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}
                  >
                    {item.componentType}
                  </TableCell>
                  <TableCell>{item.labelEn}</TableCell>
                  <TableCell
                    align='right'
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {fCurrencyQAR(item.unitPrice)}
                  </TableCell>
                  <TableCell align='center'>
                    <IconButton
                      size='small'
                      onClick={() => handleRemove(item.id)}
                    >
                      <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box
        sx={{
          mt: 3,
          p: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: '#FAF7F4',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              pr: { md: 4 },
              borderRight: { md: '1px solid' },
              borderColor: 'divider',
              mb: { xs: 2, md: 0 },
              minWidth: 150,
            }}
          >
            <Typography variant='caption' color='text.secondary'>
              Computed Total
            </Typography>
            <Typography variant='h6' sx={{ fontWeight: 'bold', my: 0.5 }}>
              {fCurrencyQAR(computedTotal)}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Sum of all components
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1, px: { md: 4 }, mb: { xs: 2, md: 0 } }}>
            <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary' }}>
              Override Price{' '}
              <Typography
                component='span'
                variant='caption'
                sx={{ color: '#d96c06ff' }}
              >
                (optional)
              </Typography>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='body2' color='text.secondary'>
                {CURRENCY}
              </Typography>
              <TextField
                size='small'
                value={overridePrice}
                placeholder='0'
                onChange={(e) => onOverridePriceChange(e.target.value)}
                InputProps={{ inputComponent: NumericFormatCustom as any }}
                sx={{
                  width: 100,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'common.white',
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ flexShrink: 0, textAlign: 'right', minWidth: 100 }}>
            <Typography variant='caption' color='text.secondary'>
              Final Price
            </Typography>
            <Typography variant='h5' sx={{ fontWeight: 'bold', mt: 0.5 }}>
              {fCurrencyQAR(finalPrice)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BundleComponentsTab;
